// config/analyticsConfig.js
import { green, red, orange, blue, cyan, deepOrange, brown, purple } from "@mui/material/colors";
import { FUNCTIONALITY } from '../../../../constants/types';

export interface AnalyticsTab {
  id: string;
  label: string;
  /** Translation id for the tab name; tabs without one show `label` as is. */
  labelId?: string;
  color: string;
  enabled: boolean;
  functionalities?: FUNCTIONALITY[];
}

const analyticsTabsList: AnalyticsTab[] = [
  {
    id: 'outpatients',
    label: 'Outpatients',
    color: blue[500],
    enabled: true,
    functionalities: [FUNCTIONALITY.OUTPATIENTS],
  },
  {
    id: 'inpatients',
    label: 'Inpatients',
    color: green[500],
    enabled: true,
    functionalities: [FUNCTIONALITY.HOSPITALIZATION],
  },
  {
    id: 'surgery',
    // Fallback only: the header shows the labelId translation, whose EN entry is the same name.
    label: 'Surgical Activity',
    labelId: 'hospital.analytics.tabs.surgery',
    color: purple[500],
    enabled: true,
    functionalities: [FUNCTIONALITY.SURGERY],
  },
  {
    id: 'laboratory',
    label: 'Laboratory',
    color: orange[500],
    enabled: true,
  },
  {
    id: 'radiology',
    label: 'Radiology',
    color: red[500],
    enabled: true,
  },
  {
    id: 'finances',
    label: 'Finances',
    color: brown[500],
    enabled: true,
    functionalities: [FUNCTIONALITY.BILLING],
  },
  {
    id: 'staff',
    label: 'Staff',
    color: cyan[500],
    enabled: false,
  },
  {
    id: 'quality',
    label: 'Quality Indicators',
    color: deepOrange[500],
    enabled: false,
  }
];

export const analyticsTabs = analyticsTabsList.filter(tab => tab.enabled);

function tabRequiredFunctionalities(tab: AnalyticsTab): FUNCTIONALITY[] {
  return tab.functionalities ?? [];
}

function isTabVisibleForInvestigation(
  tab: AnalyticsTab,
  functionalities: FUNCTIONALITY[]
): boolean {
  const required = tabRequiredFunctionalities(tab);
  if (!required.length) {
    return true;
  }
  return required.some((functionality) => functionalities.includes(functionality));
}

// Tabs visible for a given investigation. Functionality-gated tabs only appear when
// the investigation has that functionality, matching sidebar route visibility.
export const getAnalyticsTabs = (investigation: { functionalities?: FUNCTIONALITY[] } | null | undefined) => {
  const functionalities = investigation?.functionalities || [];
  return analyticsTabs.filter((tab) => isTabVisibleForInvestigation(tab, functionalities));
};

export const analyticsConfig = {
  defaultColor: blue[500],
  activeColor: blue[700],
  subtitle: '',
  defaultTab: 'outpatients',
  scrollButtons: true,
  tabVariant: 'scrollable'
};
